import * as React from "react"
import { ArrowLeft } from "lucide-react"
import { Link, useLocation, useNavigate, useSearchParams, useParams } from "react-router-dom"
import { useSWRConfig } from "swr"
import { useCreateSmithersDevice, useSmithersDevice, useUpdateSmithersDevice } from "@/hooks/use-smithers-devices"
import { SMITHERS_CLIENTS_KEY, useSmithersClients } from "@/hooks/use-smithers-clients"
import { useSmithersGlobal } from "@/hooks/use-smithers-global"
import { useToast } from "@/hooks/use-toast"
import { toSmithersDeviceRequest, toSmithersDeviceUpdate } from "@/lib/smithers-device-request"
import { getSmithersProVenueSelection } from "@/lib/smithers-provenue"
import { getSmithersDeviceListOrigin, smithersDeviceListPath } from "@/lib/smithers-device-navigation"
import type { SmithersDeviceConfig } from "@/types/smithers-device"
import { ApiErrorState } from "@/components/shared/empty-states"
import { Skeleton } from "@/components/ui/skeleton"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { SmithersProVenueField } from "@/components/smithers/smithers-provenue-field"
import type { SmithersProVenueSelection } from "@/types/provenue-instance"
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"

const identityFields = [
  { name: "clientId", label: "Client ID", placeholder: "e.g., HOU" },
  { name: "deviceId", label: "Device ID", placeholder: "e.g., gae-003" },
  { name: "sotiDeviceId", label: "SOTI Device ID", placeholder: "e.g., soti-002" },
] as const

const textConfigFields = [
  { name: "facilityCode", label: "Facility Code" },
  { name: "venueId", label: "Venue ID" },
  { name: "areaCode", label: "Area Code" },
  { name: "deviceCode", label: "Device Code" },
  { name: "nteClubCode", label: "NTE Club Code" },
] as const

const numericConfigFields = [
  { name: "matchThreshold", label: "Match Threshold", min: 0, max: 2147483647 },
  { name: "validationTimeoutMs", label: "Validation Timeout (ms)", min: 0, max: Number.MAX_SAFE_INTEGER },
] as const

type ConfigField = (typeof textConfigFields)[number]["name"] | (typeof numericConfigFields)[number]["name"]

const deviceSections = [
  { id: "smithers-device-identification", title: "Device Identification" },
  { id: "smithers-device-configuration", title: "Device Configuration" },
  { id: "smithers-provenue-connection", title: "ProVenue Connection" },
] as const

export function SmithersDeviceAddPage() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const sourceId = id ?? params.get("copyFrom")
  const numericId = sourceId === null || sourceId === undefined ? undefined : Number(sourceId)
  const validId = numericId !== undefined && Number.isSafeInteger(numericId) && numericId > 0
  const source = useSmithersDevice(validId ? numericId : undefined)
  if (sourceId != null) {
    if (!validId || source.error) return <><DashboardHeader /><main className="container mx-auto px-4 py-6"><ApiErrorState onRetry={() => void source.mutate()} /></main></>
    if (source.isLoading || !source.data) return <><DashboardHeader /><main className="container mx-auto px-4 py-6"><Skeleton className="h-64 w-full" aria-label="Loading device" /></main></>
  }
  return <SmithersDeviceForm key={`${id ? "edit" : "create"}-${sourceId ?? "new"}`} source={source.data} editing={id !== undefined} />
}

function SmithersDeviceForm({ source, editing }: { source?: SmithersDeviceConfig; editing: boolean }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const [origin] = React.useState(() => getSmithersDeviceListOrigin(location.state))
  const clientContext = origin ? origin.clientId : searchParams.get("clientId")
  const backPath = smithersDeviceListPath(clientContext)
  const backLabel = clientContext ? `Back to ${clientContext} devices` : "Back to Devices"
  const returnToDevices = () => navigate(backPath, origin ? { replace: true, state: { smithersDeviceList: origin } } : undefined)
  const { mutate } = useSWRConfig()
  const { toast } = useToast()
  const createDevice = useCreateSmithersDevice()
  const updateDevice = useUpdateSmithersDevice(editing ? source?.id : undefined)
  const clients = useSmithersClients(!editing)
  const global = useSmithersGlobal()
  const globalDefaultPlaceholder = global.isLoading ? "Loading global default…" : global.data ? "Empty global value" : "Global default unavailable"
  const isMutating = createDevice.isMutating || updateDevice.isMutating
  const submitting = React.useRef(false)
  const [submitError, setSubmitError] = React.useState<string>()
  const [showSaveConfirmation, setShowSaveConfirmation] = React.useState(false)
  const [values, setValues] = React.useState(() => ({ clientId: source?.clientId ?? searchParams.get("clientId") ?? "", deviceId: editing ? source?.deviceId ?? "" : "", sotiDeviceId: editing ? source?.sotiDeviceId ?? "" : "" }))
  const [config, setConfig] = React.useState<Record<ConfigField, { value: string; useDefault: boolean }>>(() => Object.fromEntries([...textConfigFields, ...numericConfigFields].map(({ name }) => [name, { value: source ? String(source[name]) : "", useDefault: !source || source.inheritedFromGlobal.includes(name) }])) as Record<ConfigField, { value: string; useDefault: boolean }>)
  const [validationVendor, setValidationVendor] = React.useState<"global" | "ProVenue" | "Orion">(() => !source || source.inheritedFromGlobal.includes("validationVendor") ? "global" : source.validationVendor)
  const [proVenueSelection, setProVenueSelection] = React.useState<SmithersProVenueSelection>(() => source ? getSmithersProVenueSelection(source) : { mode: "global" })
  const [initialForm] = React.useState(() => ({ values, config, validationVendor, proVenueSelection }))
  const [touched, setTouched] = React.useState<Record<string, boolean>>({})
  const selectedClient = clients.data?.find((client) => client.clientId === values.clientId)
  const hasValidClient = editing || (!clients.isLoading && !clients.error && selectedClient !== undefined)
  const clientError = editing || clients.isLoading || clients.error ? undefined
    : !values.clientId ? "Client ID is required"
    : !selectedClient ? "Select an existing client" : undefined
  const hasRequiredIdentity = editing || identityFields.every(({ name }) => values[name].trim().length > 0)
  const hasValidIdentityLength = [values.clientId, values.deviceId].every((value) => Array.from(value.trim()).length <= 100)
  const hasValidNumbers = numericConfigFields.every(({ name, min, max }) => config[name].useDefault || (config[name].value.trim() !== "" && Number.isSafeInteger(Number(config[name].value)) && Number(config[name].value) >= min && Number(config[name].value) <= max))
  const [initialUpdate] = React.useState(() => editing ? JSON.stringify(toSmithersDeviceUpdate(config, validationVendor, proVenueSelection)) : undefined)
  const hasChanges = !editing || (hasValidNumbers && JSON.stringify(toSmithersDeviceUpdate(config, validationVendor, proVenueSelection)) !== initialUpdate)
  const hasDraftChanges = JSON.stringify({ values, config, validationVendor, proVenueSelection }) !== JSON.stringify(initialForm)
  const firstInputRef = React.useRef<HTMLInputElement>(null)
  const clientInputRef = React.useRef<HTMLButtonElement>(null)


  const updateConfig = (name: ConfigField, update: Partial<{ value: string; useDefault: boolean }>) => {
    setConfig((previous) => ({ ...previous, [name]: { ...previous[name], ...update } }))
  }

  const discardChanges = () => {
    if (isMutating || submitting.current) return
    setValues(initialForm.values)
    setConfig(initialForm.config)
    setValidationVendor(initialForm.validationVendor)
    setProVenueSelection(initialForm.proVenueSelection)
    setTouched({})
    setSubmitError(undefined)
    setShowSaveConfirmation(false)
  }

  React.useEffect(() => {
    if (editing) firstInputRef.current?.focus()
    else if (!clients.isLoading) clientInputRef.current?.focus()
  }, [editing, clients.isLoading])

  const save = async () => {
    if (submitting.current || isMutating || !hasChanges || !hasRequiredIdentity || !hasValidClient || !hasValidIdentityLength || !hasValidNumbers) return
    submitting.current = true
    setSubmitError(undefined)
    try {
      const device = editing
        ? await updateDevice.trigger(toSmithersDeviceUpdate(config, validationVendor, proVenueSelection))
        : await createDevice.trigger(toSmithersDeviceRequest(values, config, validationVendor, proVenueSelection))
      await mutate((key) => Array.isArray(key) && key[0] === "smithers" && key[1] === "devices", undefined, { revalidate: true })
      await mutate(SMITHERS_CLIENTS_KEY)
      toast({ variant: "success", title: editing ? "Device updated successfully" : "Device created successfully", description: `Device ${device.deviceId} has been ${editing ? "updated" : "added"}.` })
      if (editing) {
        await mutate(["smithers", "devices", "detail", device.id], device, { revalidate: false })
        navigate(`/smithers/devices/${device.id}${clientContext === null ? "" : `?${new URLSearchParams({ clientId: clientContext })}`}`, {
          replace: true, state: origin ? { fromSmithersDeviceList: true, smithersDeviceList: origin } : undefined,
        })
      } else returnToDevices()
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to save device. Please try again."
      setSubmitError(message)
      toast({ variant: "error", title: editing ? "Failed to update device" : "Failed to create device", description: message })
    } finally {
      submitting.current = false
    }
  }

  return (
    <>
      <DashboardHeader />
      <main className="container mx-auto flex flex-col gap-6 px-4 py-6">
        <div>
          <Button type="button" variant="ghost" size="sm" disabled={isMutating} onClick={() => { if (!submitting.current) returnToDevices() }} className="-ml-2 mb-4 text-muted-foreground"><ArrowLeft data-icon="inline-start" />{backLabel}</Button>
          <h1 className="text-2xl font-semibold text-foreground">{editing ? "Edit Smithers Device" : source ? "Copy Smithers Device" : "Add Smithers Device"}</h1>
        </div>
        <nav aria-label="Configuration sections" className="flex gap-2 overflow-x-auto pb-1 xl:hidden">
          {deviceSections.map((section) => (
            <Button key={section.id} asChild variant="outline" size="sm" className="h-8 shrink-0 bg-card text-xs text-muted-foreground">
              <a href={`#${section.id}`}>{section.title}</a>
            </Button>
          ))}
        </nav>
        <div className="grid w-full max-w-[1440px] items-start gap-6 xl:grid-cols-[220px_minmax(0,1fr)]">
          <Card className="sticky top-[132px] hidden max-h-[calc(100vh-156px)] overflow-y-auto xl:block">
            <CardHeader className="px-4 pb-3 pt-4">
              <CardTitle className="text-sm">Jump to section</CardTitle>
              <CardDescription className="text-xs">{deviceSections.length} configuration groups</CardDescription>
            </CardHeader>
            <CardContent className="px-2 pb-3 pt-0">
              <nav aria-label="Configuration section shortcuts" className="flex flex-col gap-0.5">
                {deviceSections.map((section) => (
                  <Button key={section.id} asChild variant="ghost" size="sm" className="h-auto w-full justify-start whitespace-normal px-2 py-1.5 text-left text-xs font-normal text-muted-foreground hover:text-foreground">
                    <a href={`#${section.id}`}>{section.title}</a>
                  </Button>
                ))}
              </nav>
            </CardContent>
          </Card>
          <form
            className="flex min-w-0 w-full flex-col gap-6"
            onSubmit={(event) => {
              event.preventDefault()
              if (submitting.current || !hasChanges) return
              setTouched(Object.fromEntries([...identityFields, ...numericConfigFields].map(({ name }) => [name, true])))
              if (!hasRequiredIdentity || !hasValidClient || !hasValidIdentityLength || !hasValidNumbers) return
              if (editing) {
                setShowSaveConfirmation(true)
                return
              }
              void save()
            }}
          >
            {submitError && <Alert variant="destructive"><AlertTitle>{editing ? "Couldn’t update device" : "Couldn’t create device"}</AlertTitle><AlertDescription>{submitError}</AlertDescription></Alert>}
            <fieldset disabled={isMutating} className="flex min-w-0 flex-col gap-6">
            <Card id="smithers-device-identification" className="scroll-mt-20 md:scroll-mt-[132px]">
              <CardHeader className="border-b border-border bg-muted/40 px-5 py-4">
                <CardTitle className="text-base"><h2>Device Identification</h2></CardTitle>
                <CardDescription className="text-xs">3 properties</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-x-8 gap-y-4 p-6 md:grid-cols-2">
                {identityFields.map(({ name, label, placeholder }, index) => {
                  const error = name === "clientId" && !editing
                    ? touched.clientId || Boolean(values.clientId) ? clientError : undefined
                    : editing || !touched[name] ? undefined : !values[name].trim() ? `${label} is required` : name !== "sotiDeviceId" && Array.from(values[name].trim()).length > 100 ? `${label} cannot be longer than 100 characters` : undefined
                  return (
                    <div key={name} className="flex flex-col gap-2" data-invalid={error ? true : undefined}>
                      <Label htmlFor={`smithers-${name}`}>
                        {label} {!editing && <span aria-hidden="true" className="text-destructive">*</span>}
                      </Label>
                      {name === "clientId" && !editing ? <>
                        <Select name="clientId" required value={selectedClient?.clientId ?? ""} onValueChange={(clientId) => {
                          if (!clients.data?.some((client) => client.clientId === clientId)) return
                          setValues((previous) => ({ ...previous, clientId }))
                          setTouched((previous) => ({ ...previous, clientId: true }))
                        }} disabled={isMutating || clients.isLoading || Boolean(clients.error) || clients.data?.length === 0}>
                          <SelectTrigger ref={clientInputRef} id="smithers-clientId" aria-required="true" aria-invalid={Boolean(error)} aria-describedby={error ? "smithers-clientId-error" : undefined} onBlur={() => setTouched((previous) => ({ ...previous, clientId: true }))}>
                            <SelectValue placeholder="Select a client..." />
                          </SelectTrigger>
                          <SelectContent><SelectGroup>{clients.data?.map((client) => <SelectItem key={client.id} value={client.clientId}>{client.clientId}</SelectItem>)}</SelectGroup></SelectContent>
                        </Select>
                        {clients.isLoading && <p role="status" className="text-sm text-muted-foreground">Loading clients…</p>}
                        {clients.error && <Alert variant="destructive"><AlertTitle>Couldn’t load clients</AlertTitle><AlertDescription><Button type="button" variant="outline" onClick={() => void clients.mutate()}>Retry clients</Button></AlertDescription></Alert>}
                        {!clients.isLoading && !clients.error && clients.data?.length === 0 && <p className="text-sm text-muted-foreground">No clients available. <Link to="/smithers/clients/new" className="underline underline-offset-4">Add a client</Link> first.</p>}
                      </> : <Input
                        ref={index === 0 ? firstInputRef : undefined}
                        id={`smithers-${name}`}
                        name={name}
                        required={!editing}
                        readOnly={editing}
                        pattern={".*\\S.*"}
                        value={values[name]}
                        placeholder={placeholder}
                        onChange={(event) => setValues((previous) => ({ ...previous, [name]: event.target.value }))}
                        onBlur={() => setTouched((previous) => ({ ...previous, [name]: true }))}
                        aria-invalid={Boolean(error)}
                        aria-describedby={error ? `smithers-${name}-error` : undefined}
                      />}
                      {error && (
                        <p id={`smithers-${name}-error`} role="alert" className="text-sm text-destructive">{error}</p>
                      )}
                    </div>
                  )
                })}
              </CardContent>
            </Card>
            <Card id="smithers-device-configuration" className="scroll-mt-20 md:scroll-mt-[132px]">
              <CardHeader className="border-b border-border bg-muted/40 px-5 py-4">
                <CardTitle className="text-base"><h2>Device Configuration</h2></CardTitle>
                <CardDescription className="text-xs">8 properties</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-x-8 gap-y-6 p-6 md:grid-cols-2">
                <p className="text-sm text-muted-foreground md:col-span-2">
                  Use defaults or set device overrides. An empty text override keeps the value empty.
                </p>
                {global.isLoading && <p role="status" className="text-sm text-muted-foreground md:col-span-2">Loading global defaults…</p>}
                {global.error && <Alert variant="destructive" className="md:col-span-2">
                  <AlertTitle>Couldn’t load global defaults</AlertTitle>
                  <AlertDescription>
                    <p>Default previews are unavailable. You can still use global defaults or set overrides.</p>
                    <Button type="button" variant="outline" onClick={() => void global.mutate()}>Retry defaults</Button>
                  </AlertDescription>
                </Alert>}
                <div className="flex flex-col gap-2">
                  <Label htmlFor="smithers-validationVendor">Validation Vendor</Label>
                  <Select disabled={validationVendor === "global"} value={validationVendor === "global" ? global.data?.validationVendor ?? "" : validationVendor} onValueChange={(value) => { if (value === "ProVenue" || value === "Orion") setValidationVendor(value) }}>
                    <SelectTrigger id="smithers-validationVendor"><SelectValue placeholder="Default vendor" /></SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="ProVenue">ProVenue</SelectItem>
                        <SelectItem value="Orion">Orion</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Checkbox id="smithers-validationVendor-default" checked={validationVendor === "global"} aria-label="Use default for Validation Vendor" onCheckedChange={(checked) => setValidationVendor(checked === true ? "global" : global.data?.validationVendor ?? "ProVenue")} />
                    <label htmlFor="smithers-validationVendor-default" className="cursor-pointer select-none whitespace-nowrap text-xs text-muted-foreground">Use default</label>
                  </div>
                </div>
                {textConfigFields.map(({ name, label }) => (
                  <div key={name} className="flex flex-col gap-2">
                    <Label htmlFor={`smithers-${name}`}>{label}</Label>
                    <Input
                      id={`smithers-${name}`}
                      name={name}
                      value={config[name].useDefault ? global.data?.[name] ?? "" : config[name].value}
                      disabled={config[name].useDefault}
                      placeholder={config[name].useDefault ? globalDefaultPlaceholder : "Empty value allowed"}
                      onChange={(event) => updateConfig(name, { value: event.target.value })}
                    />
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`smithers-${name}-default`}
                        checked={config[name].useDefault}
                        onCheckedChange={(checked) => updateConfig(name, { useDefault: checked === true })}
                        aria-label={`Use default for ${label}`}
                      />
                      <label htmlFor={`smithers-${name}-default`} className="cursor-pointer select-none whitespace-nowrap text-xs text-muted-foreground">Use default</label>
                    </div>
                  </div>
                ))}
                {numericConfigFields.map(({ name, label, min, max }) => {
                  const { value, useDefault } = config[name]
                  const number = Number(value)
                  const invalid = !useDefault && touched[name] && (
                    !value.trim() || !Number.isSafeInteger(number) || number < min || number > max
                  )
                  return (
                    <div key={name} className="flex flex-col gap-2" data-invalid={invalid ? true : undefined}>
                      <Label htmlFor={`smithers-${name}`}>{label}</Label>
                      <Input
                        id={`smithers-${name}`}
                        name={name}
                        type="number"
                        min={min}
                        max={max}
                        step={1}
                        value={useDefault ? global.data?.[name] ?? "" : value}
                        disabled={useDefault}
                        placeholder={useDefault ? globalDefaultPlaceholder : "Enter an integer"}
                        onChange={(event) => updateConfig(name, { value: event.target.value })}
                        onBlur={() => setTouched((previous) => ({ ...previous, [name]: true }))}
                        aria-invalid={Boolean(invalid)}
                        aria-describedby={invalid ? `smithers-${name}-error` : undefined}
                      />
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`smithers-${name}-default`}
                          checked={useDefault}
                          onCheckedChange={(checked) => updateConfig(name, { useDefault: checked === true })}
                          aria-label={`Use default for ${label}`}
                        />
                        <label htmlFor={`smithers-${name}-default`} className="cursor-pointer select-none whitespace-nowrap text-xs text-muted-foreground">Use default</label>
                      </div>
                      {invalid && (
                        <p id={`smithers-${name}-error`} role="alert" className="text-sm text-destructive">
                          {label} must be an integer between {min} and {max}.
                        </p>
                      )}
                    </div>
                  )
                })}
              </CardContent>
            </Card>
            <Card id="smithers-provenue-connection" className="scroll-mt-20 md:scroll-mt-[132px]">
              <CardHeader className="border-b border-border bg-muted/40 px-5 py-4"><CardTitle className="text-base"><h2>ProVenue Connection</h2></CardTitle><CardDescription className="text-xs">3 properties</CardDescription></CardHeader>
              <CardContent className="grid gap-x-8 gap-y-4 p-6 md:grid-cols-2">
                <SmithersProVenueField value={proVenueSelection} onChange={setProVenueSelection} globalConfig={global.data} />
              </CardContent>
              <CardFooter className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-end">
                <div className="flex flex-wrap gap-3 sm:justify-end">
                  <Button type="button" variant="outline" onClick={discardChanges} disabled={(!hasDraftChanges && !submitError) || isMutating}>Discard changes</Button>
                  <Button type="submit" disabled={!hasRequiredIdentity || !hasValidClient || !hasValidIdentityLength || !hasValidNumbers || !hasChanges || isMutating}>{isMutating ? editing ? "Saving…" : "Creating…" : editing ? "Save Changes" : "Create Device"}</Button>
                </div>
              </CardFooter>
            </Card>
            </fieldset>
          </form>
        </div>
      </main>
      <AlertDialog open={showSaveConfirmation} onOpenChange={setShowSaveConfirmation}>
        <AlertDialogContent className="max-w-[400px] border-border bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Save device configuration changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">Are you sure you want to save these changes to this device?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void save()}>Save changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
